/**
 * The name-matching pill (LED-1155) — ONE pattern for every person or company
 * field a walk fills from what was SAID: an assignee, a manpower company.
 *
 * A name heard on site is a hint, not an identity. The directory matcher
 * either settled on somebody, found several people about equally likely, or
 * found nobody. Each gets its own resting face, so a reviewer can tell them
 * apart at a glance without an explainer:
 *
 *  - **matched** — the person or company, shown like any other value.
 *  - **ambiguous** — small face cards (initials) of the top candidates. Tap a
 *    face to pick that person; tap the label to open the full directory.
 *  - **missing** — `No "Brian Smith" in directory` in a quiet warning
 *    treatment. Tapping opens the directory picker with the heard name
 *    already typed into its search, because that is the first thing anybody
 *    would search for.
 *  - **empty** — nothing was said: `Not set`, like every other empty pill.
 *
 * Which state applies is `nameMatchOf`'s answer, and it defers to the
 * phone's own rule (`assigneeMode` → `assigneeDisplay`) for locked vs
 * suggesting, so the console and the phone cannot disagree about whether a
 * field is settled. This file adds only the "heard but not found" case the
 * server now projects (`assigneeHeardName` / `companyHeardName`).
 *
 * Reusable by design: the Review card and the Daily log detail pane both draw
 * it. It owns no data fetching — the host opens its own picker (`onOpen`,
 * with the text to pre-fill) and commits a picked candidate (`onPick`).
 */
import type { CSSProperties, ReactNode, Ref } from "react";
import type { ConsoleAssigneeSuggestion } from "../api";
import { assigneeMode } from "./assigneeSuggestion";
import { initialsOf } from "./chrome";
import { C, FONT_MONO, RV } from "../tokens";

export interface NameMatchCandidate {
  /** What a pick commits (the server already collapsed it to one id). */
  id: string;
  name: string;
  /** Company or job title, for the face's tooltip. */
  sub?: string;
  /** Calibrated confidence, when the matcher reported one. */
  pct?: number;
}

export type NameMatch =
  | { state: "matched"; name: string }
  | { state: "ambiguous"; candidates: NameMatchCandidate[] }
  | { state: "missing"; heard: string }
  | { state: "empty" };

/** How many faces a pill shows at most. */
export const NAME_MATCH_FACES = 3;

function sameName(a: string, b: string): boolean {
  const key = (value: string) => value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
  return key(a) === key(b);
}

/**
 * The field's state, from what the record carries.
 *
 * @param value The field's current text (the resolved name, or the heard one).
 * @param suggestion The directory match behind it, when one ran.
 * @param heardName The name the walk said that the directory lacks.
 * @param selectedRemoteId A pick made in THIS editing session: `undefined`
 *   defers to the record, `""` is an explicit clear.
 * @param directory Whether the project has a directory to match against at
 *   all. Without one, free text IS the value.
 */
export function nameMatchOf(opts: {
  value: string;
  suggestion?: ConsoleAssigneeSuggestion;
  heardName?: string;
  selectedRemoteId?: string;
  directory?: boolean;
}): NameMatch {
  const value = opts.value.trim();
  // A pick made here wins outright: it is a directory identity.
  if (opts.selectedRemoteId && value) return { state: "matched", name: value };
  if (opts.selectedRemoteId === "" && !value) return { state: "empty" };
  if (opts.directory === false) return value ? { state: "matched", name: value } : { state: "empty" };
  // Heard, searched, and nobody found — while the field still reads the
  // heard name. Once somebody types or picks something else, it is theirs.
  if (opts.heardName && value && sameName(value, opts.heardName)) {
    return { state: "missing", heard: opts.heardName.trim() };
  }
  const mode = assigneeMode({ suggestion: opts.suggestion, value, selectedRemoteId: opts.selectedRemoteId });
  if (mode === "locked") {
    const selected = opts.selectedRemoteId === undefined ? opts.suggestion?.selected?.name : undefined;
    return { state: "matched", name: selected || value };
  }
  if (mode === "suggesting") {
    const candidates = (opts.suggestion?.candidates ?? []).slice(0, NAME_MATCH_FACES).map((candidate) => ({
      id: candidate.remoteId,
      name: candidate.name,
      ...(candidate.company || candidate.jobTitle ? { sub: candidate.company || candidate.jobTitle } : {}),
      ...(typeof candidate.confidencePercent === "number" ? { pct: Math.round(candidate.confidencePercent) } : {}),
    }));
    if (candidates.length > 0) return { state: "ambiguous", candidates };
  }
  return { state: "empty" };
}

/** The text the host's directory picker should open with: the heard name for
 *  a `missing` field, nothing otherwise. */
export function nameMatchQuery(match: NameMatch): string {
  return match.state === "missing" ? match.heard : "";
}

export interface NameMatchPillProps {
  match: NameMatch;
  /** The field's name — `Assignee`, `Company`. */
  label: string;
  /** The field's glyph, drawn first. */
  mark?: ReactNode;
  /** Open the host's directory picker, pre-filled with `query`. */
  onOpen: (query: string) => void;
  /** Pick one of an ambiguous match's candidates. */
  onPick?: (candidate: NameMatchCandidate) => void;
  /** The picker's open state, for `aria-expanded`. */
  open?: boolean;
  /** The main button, so a host's popover can anchor on it. */
  triggerRef?: Ref<HTMLButtonElement>;
  disabled?: boolean;
  testId?: string;
  /** The main button's test id (defaults to `{testId}-open`). */
  buttonTestId?: string;
  /** The pill's shape. Defaults to the review card's pill. */
  style?: CSSProperties;
}

const PILL: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 34,
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  padding: "0 12px 0 10px",
  borderRadius: 999,
  background: RV.bubble,
  border: "1px solid rgba(20,17,12,.08)",
  fontSize: 13,
  color: C.ink,
  whiteSpace: "nowrap",
};

const BARE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  minWidth: 0,
  maxWidth: "100%",
  padding: 0,
  margin: 0,
  border: 0,
  background: "transparent",
  font: "inherit",
  color: "inherit",
  cursor: "pointer",
  textAlign: "left",
};

const KEY: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "1.1px",
  color: C.sub,
  textTransform: "uppercase",
  flex: "none",
};

export function NameMatchPill({ match, label, mark, onOpen, onPick, open, triggerRef, disabled, testId, buttonTestId, style }: NameMatchPillProps) {
  const missing = match.state === "missing";
  const root: CSSProperties = {
    ...PILL,
    ...style,
    ...(missing ? { background: RV.warnBg, borderColor: RV.warnLine, borderStyle: "solid", borderWidth: 1 } : {}),
    ...(match.state === "ambiguous" ? { paddingRight: 4 } : {}),
  };
  const query = nameMatchQuery(match);
  const ariaValue =
    match.state === "matched" ? match.name
    : match.state === "ambiguous" ? `${match.candidates.length} possible matches`
    : match.state === "missing" ? `no "${match.heard}" in directory`
    : "not set";
  return (
    <span
      role="group"
      aria-label={label}
      data-testid={testId ?? "name-match"}
      data-match={match.state}
      style={root}
    >
      <button
        ref={triggerRef}
        type="button"
        className="name-match-open"
        data-testid={buttonTestId ?? `${testId ?? "name-match"}-open`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${ariaValue}`}
        title={missing ? `Search the directory for “${match.heard}”` : match.state === "matched" ? match.name : label}
        disabled={disabled}
        onClick={() => onOpen(query)}
        style={{ ...BARE, cursor: disabled ? "default" : "pointer" }}
      >
        {mark}
        {match.state === "matched" ? (
          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontWeight: 700 }}>{match.name}</span>
        ) : match.state === "missing" ? (
          <span data-testid="name-match-missing" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600, color: RV.warnInk }}>
            {`No “${match.heard}” in directory`}
          </span>
        ) : (
          <>
            <span style={KEY}>{label}</span>
            {match.state === "empty" && <span style={{ fontWeight: 500, color: RV.meta }}>Not set</span>}
          </>
        )}
      </button>
      {match.state === "ambiguous" && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 3, flex: "none" }}>
          {match.candidates.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className="name-match-face"
              data-testid="name-match-face"
              data-candidate-id={candidate.id}
              aria-label={`Pick ${candidate.name}${candidate.sub ? `, ${candidate.sub}` : ""}`}
              title={[candidate.name, candidate.sub, candidate.pct !== undefined ? `${candidate.pct}%` : null].filter(Boolean).join(" · ")}
              disabled={disabled || !onPick}
              onClick={() => onPick?.(candidate)}
              style={{
                width: 26,
                height: 26,
                flex: "none",
                padding: 0,
                borderRadius: 13,
                border: `1.5px solid ${RV.faceRing}`,
                boxShadow: "0 0 0 1px rgba(20,17,12,.12)",
                background: RV.faceBg,
                color: C.ink,
                fontFamily: "inherit",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "-0.2px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "default" : "pointer",
              }}
            >
              {initialsOf(candidate.name)}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}
